import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import clinicService from "../../services/clinic.service";
import "../reputation/reputation.css";
import "./welcome.css";
import "./appointments.css";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatTime = (timeString) => {
  if (!timeString) return "—";
  const options = { hour: "numeric", minute: "numeric", hour12: true };
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString(undefined, options);
};

function AppointmentCancel() {
  const { clinicId, appointmentId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appointment, setAppointment] = useState(null);

  useEffect(() => {
    if (!clinicId || !appointmentId) {
      setLoading(false);
      setError("This cancellation link is incomplete. Please use the link from your reminder email.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    clinicService
      .declineAppointment(clinicId, appointmentId)
      .then((res) => {
        if (!cancelled) {
          setAppointment(res.data || null);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log("Failed to decline appointment", err);
        if (!cancelled) {
          const message =
            err?.response?.data?.message ||
            "We could not cancel your appointment. The link may be invalid or the appointment may have already passed.";
          setError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clinicId, appointmentId]);

  if (loading) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel welcome-loading">
          <CircularProgress size={28} />
          <p className="rep-copy" style={{ margin: 0 }}>
            Cancelling your appointment…
          </p>
        </section>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel">
          <div className="rep-done">
            <div className="rep-done-mark appt-done-mark--cancel" aria-hidden="true">
              <i className="bi bi-exclamation-circle" />
            </div>
            <h2 className="rep-title">Unable to cancel</h2>
            <p className="rep-copy" style={{ marginBottom: 0 }}>
              {error || "We could not cancel your appointment. Please contact your clinic for help."}
            </p>
          </div>
        </section>
      </div>
    );
  }

  const patient = appointment.patient || {};

  return (
    <div className="welcome-stack">
      <section className="rep-panel">
        <div className="rep-done">
          <div className="rep-done-mark appt-done-mark--cancel" aria-hidden="true">
            <i className="bi bi-x-lg" />
          </div>
          <h2 className="rep-title">Appointment cancelled</h2>
          <p className="rep-copy" style={{ marginBottom: 0 }}>
            Your appointment has been cancelled.
          </p>
        </div>
      </section>

      <section className="rep-panel">
        <Alert severity="info" sx={{ mb: 0 }}>
          We&apos;re sorry to hear there is a conflict with this appointment. One of
          our staff members will contact you shortly to arrange a more convenient
          time.
        </Alert>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Appointment details</h3>
        <p className="welcome-section-copy">Patient appointment</p>
        <div className="appt-details">
          <div className="appt-row">
            <span className="appt-label">Date</span>
            <span className="appt-value">{formatDate(appointment.dateAppointment)}</span>
          </div>
          <div className="appt-row">
            <span className="appt-label">Time</span>
            <span className="appt-value">{formatTime(appointment.startTime)}</span>
          </div>
        </div>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Patient</h3>
        <p className="welcome-section-copy">Who this cancelled appointment was for.</p>
        <div className="appt-patient-list">
          <div className="appt-patient-card">
            <p className="appt-patient-name">
              {patient.firstName} {patient.lastName}
            </p>
            {patient.preferredName ? (
              <p className="appt-patient-preferred">
                Preferred name: {patient.preferredName}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AppointmentCancel;
